import PlusSignIcon from 'components/icons/PlusSignIcon';
import Tooltip from 'components/Tooltip';
import Image from 'next/image';
import InlineTooltip from 'components/InlineTooltip';
import HotspotTooltip from 'components/HotspotTooltip';
const cardImages = [
  {
    src: '/ma-timeline-hospital.svg',
    alt: 'Medicare Advantage hospital costs chart',
    caption:
      'Infographic shows how hospital costs might be covered with Medicare Advantage. First, Daniel pays the $200 deductible. For days 1 through 7, he pays a $395 per day copay. Starting day 8, the hospital stay is covered 100%.',
    width: 1060,
    height: 600,
  },
  {
    src: '/ma-donut-step-1.svg',
    alt: 'Medicare Advantage specialist visit costs chart',
    caption:
      "Infographic showing what the cost of a visit to an in-network orthopedic specialist for a pain in Sarah's wrist with Medicare Advantage could be. The total cost for the visit before insurance is $502. Sarah first pays a $200 deductible and a $25 copay. Medicare Advantage covers the remaining cost ($277).",
    width: 1060,
    height: 630,
  },
  {
    src: '/ma-donut-step-2-and-3.svg',
    alt: 'Medicare Advantage MRI and outpatient surgery costs chart',
    caption:
      "Infographic showing what the cost of an MRI and outpatient surgery for Sarah's wrist with Medicare Advantage could be. Sarah must get pre-authorization from her insurance before getting these services. The total cost of the MRI before insurance is $3,000. Sarah pays a $395 copay. Medicare Advantage covers the remaining cost of the MRI ($2,605). The total cost of the surgery before insurance is $5,750. Sarah pays the 15% excess charge that is not covered by Medicare Advantage ($750). She also pays a copay of $400. Medicare Advantage covers the remaining cost of the surgery ($4,600).",
    width: 1060,
    height: 795,
  },
  {
    src: '/ma-rx-calendar.svg',
    alt: 'Medicare Advantage rx costs chart',
    caption:
      "Infographic showing the cost of prescription drugs with a Medicare Advantage plan. Daniel is prescribed a Tier 1 (generic) drug that costs $24, before insurance, and 2 Tier 5 (brand-name) drug that cost $890 in total, before insurance. In January, Daniel pays a copay ($10) and a deductible ($615). The amount remaining is $275. Daniel pays 20% coinsurance ($55) and the plan covers the remaining 80% coinsurance ($220). In January, Daniel pays $680 in total. From February through August, Daniel pays a copay ($10) and 20% coinsurance ($178) and the plan covers the remaining 80% coinsurance ($712). For these months, he's paying $188 each month in total. In September, Daniel pays a copay ($10). He reaches his out-of-pocket maximum ($2,100) so he only pays $104 in coinsurance. The plan pays 100% for the rest of the year and Daniel doesn't pay anything.",
    width: 1060,
    height: 1315,
  },
];

const MAExplainer = () => {
  return (
    <section className="lfe-page-container coverage-explainer med-advantage">
      {/* Header */}
      <div className="coverage-explainer-header">
        <h2>Medicare Advantage: Bundle Parts A, B, and D into 1 plan</h2>
        <div className="versus-circle">
          <h2>vs</h2>
        </div>
        <h2>Original Medicare (Parts A and B) Alone</h2>
        <div className="subtitle">
          <>
            <p>
              The following details do not represent a specific Medicare
              Advantage plan. The costs are generalized features you might find
              in a plan. Costs for the Medicare Advantage plan premium and
              medical deductible are averages from the{' '}
              <a
                href="https://www.ncoa.org/article/what-are-the-costs-of-medicare-advantage-part-c/"
                target="_blank"
              >
                National Council on Aging
              </a>
              . Primary Care and Specialist visit copay estimates come from{' '}
              <a
                href="https://boomerbenefits.com/original-medicare-vs-medicare-advantage/"
                target="_blank"
              >
                BoomerBenefits
              </a>
              .The inpatient hospital copay estimate comes from{' '}
              <a
                href="https://themedicarefamily.com/blog/stay-in-the-hospital-under-medicare/"
                target="_blank"
              >
                The Medicare Family
              </a>
              . Sample costs for MRI and Outpatient Surgery copays come from a
              random sampling of 20 different Medicare Advantage plans across
              the country. And the average Medical Out-of-Pocket Max comes from{' '}
              <a
                href="https://www.kff.org/medicare/medicare-advantage-premiums-out-of-pocket-limits-supplemental-benefits-and-prior-authorization/"
                target="_blank"
              >
                KFF
              </a>
              . Rx details come from{' '}
              <a
                href="https://www.kff.org/medicare/key-facts-about-medicare-part-d-enrollment-premiums-and-cost-sharing-in-2025/"
                target="_blank"
              ></a>
              KFF, along with medicareresources.org which identifies that
              deductibles will most likely apply to tiers 3-5. Actual plan
              premiums, costs, and details, as well as the costs for provider
              visits and services, may vary in your area.
            </p>
            <p>
              The examples here assume using in-network providers and services,
              as well as getting prior authorization, when required.
            </p>
          </>
        </div>
      </div>

      {/* Coverage details card */}
      <div className="lfe-card coverage-card">
        <div className="lfe-card-header centered">
          <h3>Coverage Details</h3>
        </div>
        <div className="lfe-card-content">
          <div className="card-col-container">
            {/* Left column */}
            <div className="card-col">
              {/* MA card */}
              <div className="itemized-costs-card ma-plan">
                <div className="itemized-costs-content">
                  <table>
                    <caption>Medicare Advantage Plan HMO</caption>
                    <tbody>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>{'$34.50'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Deductible"
                            tooltipText={'deductible'}
                          />
                          :
                        </th>
                        <td>
                          <b>{'$0'}</b>
                        </td>
                      </tr>
                      <tr className="list-header">
                        <th scope="colgroup" colSpan={2}>
                          Medical{' '}
                          <InlineTooltip text="Copays" tooltipText={'copay'} />:
                        </th>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          PCP Visit:
                        </th>
                        <td>
                          <b>{'$10'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          Specialist Visit:
                        </th>
                        <td>
                          <b>{'$20'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          {'Inpatient Hospital, days 1-6:'}
                        </th>
                        <td>
                          <b>{'$300'}</b>/day
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          {'day 7'}
                        </th>
                        <td>
                          <b>$0</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          Diagnostic radiology, such as MRIs:
                        </th>
                        <td>
                          <b>{'$225'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row" className="list-item">
                          Outpatient Surgery:
                        </th>
                        <td>
                          <b>$400</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Medical Out-of-Pocket Maximum:"
                            tooltipText="oopmCurrentYear"
                          />
                        </th>
                        <td>
                          <b>{'$5,320'}</b>
                        </td>
                      </tr>
                      <tr className="section-header rx-deductible">
                        <th scope="colgroup" colSpan={2}>
                          Rx Deductible
                        </th>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Rx Tiers"
                            tooltipText={'rxTiers'}
                          />{' '}
                          1 and 2:
                        </th>
                        <td>
                          <b>None</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Rx Tiers"
                            tooltipText={'rxTiers'}
                          />{' '}
                          3, 4, and 5:
                        </th>
                        <td>
                          <b>{'$328'}</b>
                          <Tooltip
                            horizontalAlign="right"
                            color="blue"
                            subject="Rx tiers"
                          >
                            {
                              "Covered prescriptions are divided up into groups, or tiers, on a list called a formulary. Different tiers may have different levels of coverage. A plan's Summary of Benefits will offer more detail on the plan's formulary."
                            }
                          </Tooltip>
                        </td>
                      </tr>
                      <tr className="list-header">
                        <th scope="colgroup" colSpan={2}>
                          Rx{' '}
                          <InlineTooltip text="Copays" tooltipText={'copay'} />:
                        </th>
                      </tr>
                      <tr>
                        <th scope="row" className="sub-item">
                          <InlineTooltip
                            text="Rx Tiers"
                            tooltipText={'rxTiers'}
                          />{' '}
                          1 and 2:
                        </th>
                        <td>
                          <b>{'$0-$10'}</b>
                        </td>
                      </tr>
                      <tr className="list-header">
                        <th scope="colgroup" colSpan={2}>
                          <InlineTooltip
                            text="Rx Coinsurance"
                            tooltipText={'coinsurance'}
                          />
                          :
                        </th>
                      </tr>
                      <tr>
                        <th scope="row" className="sub-item">
                          <InlineTooltip
                            text="Rx Tiers"
                            tooltipText={'rxTiers'}
                          />{' '}
                          3, 4, and 5:
                        </th>
                        <td>
                          <b>{'25%'}</b>
                        </td>
                      </tr>
                      <tr>
                        <th scope="row">
                          <InlineTooltip
                            text="Rx Out-of-Pocket Maximum"
                            tooltipText={'rxOopm'}
                          />
                          :
                        </th>
                        <td>
                          <b>{'$2,100'}</b>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Plus sign column */}
            <div className="plus-sign">
              <PlusSignIcon size="25" color="blue" />
            </div>

            {/* Right column */}
            <div className="card-col">
              {/* Part A and B card */}
              <div className="itemized-costs-card original-medicare">
                <div className="itemized-costs-content">
                  <table>
                    <tbody>
                      <tr className="section-header">
                        <th scope="colgroup" colSpan={2}>
                          Part A
                        </th>
                      </tr>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>$0</b>
                        </td>
                      </tr>
                      <tr className="section-header part-b">
                        <th scope="colgroup" colSpan={2}>
                          Part B
                        </th>
                      </tr>
                      <tr>
                        <th scope="row">
                          Monthly{' '}
                          <InlineTooltip
                            text="Premium"
                            tooltipText={'premium'}
                          />
                          :
                        </th>
                        <td>
                          <b>$202.90</b> (minimum)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card -  Hospital */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Advantage Could Apply to Daniel&rsquo;s
            Hospital Stay (in 2026)
          </h3>
        </div>

        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <p className="example-content">
              Daniel is admitted to the hospital for cancer treatment. His
              treatment requires hospitalization for over 150 days.
            </p>
          </div>

          {/* Timeline chart */}
          <figure className="annotated-image">
            <Image
              src={cardImages[0].src}
              alt={cardImages[0].alt}
              width={cardImages[0].width}
              height={cardImages[0].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[0].caption}
            </figcaption>

            <HotspotTooltip
              x={8}
              y={40}
              text="Hospital care"
              tooltipText="inpatientHospital"
              direction="left"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Hospital Stay</h3>
            </div>

            <div className="card-col-container">
              {/* Left column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    {'Medicare Advantage Plan with Parts A and B Premiums'}
                  </h4>
                </div>

                {/* MA card */}
                <div className="itemized-costs-card ma-plan">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Advantage</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$0'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Copays"
                              tooltipText={'copay'}
                            />{' '}
                            {'Total for Days 1-6:'}
                          </th>
                          <td>
                            <b>{'$1,800'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total for 150 days:</p>
                    <p className="total-number">{'$4,649'}</p>
                  </div>
                  <p>(then coverage continues at $0/day)</p>
                </div>
              </div>

              {/* Versus */}
              <div className="versus-circle">
                <h3>vs</h3>
              </div>

              {/* Right column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$1,736</b> per benefit period
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip text="Copay" tooltipText={'copay'} />{' '}
                            Total for Days 61-150:
                          </th>
                          <td>
                            <b>$65,100</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total for 150 days:</p>
                    <p className="total-number">{'$69,271'}</p>
                  </div>
                  <p>(then coverage ends)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card - Medical Services */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Advantage Could Apply to Sarah&rsquo;s
            Medical Services (in 2026)
          </h3>
        </div>
        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <ol className="example-content small">
              <li>Sarah visits her orthopedist for a pain in her wrist.</li>
              <li>
                {
                  'The doctor orders an MRI, confirming she needs a total shoulder replacement.'
                }
              </li>
              <li>An outpatient surgery is performed as treatment.</li>
            </ol>
          </div>

          {/* Donut charts */}
          <figure className="annotated-image">
            <Image
              src={cardImages[1].src}
              alt={cardImages[1].alt}
              width={cardImages[1].width}
              height={cardImages[1].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[1].caption}
            </figcaption>
            <HotspotTooltip
              x={61}
              y={26}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
            <HotspotTooltip
              x={74}
              y={42}
              text="copay"
              tooltipText="copay"
              direction="left"
            />
            <HotspotTooltip
              x={57}
              y={66}
              text="deductible"
              tooltipText="deductible"
              direction="left"
            />
            <HotspotTooltip
              x={70}
              y={66}
              text="copay"
              tooltipText="copay"
              direction="left"
            />
          </figure>
          <figure className="annotated-image">
            <Image
              src={cardImages[2].src}
              alt={cardImages[2].alt}
              width={cardImages[2].width}
              height={cardImages[2].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[2].caption}
            </figcaption>
            <HotspotTooltip x={25} y={24} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={69}
              y={20}
              text="15% excess charge"
              tooltipText="excessCharge"
              direction="left"
            />
            <HotspotTooltip
              x={82}
              y={37}
              text="copay"
              tooltipText="copay"
              direction="left"
            />
            <HotspotTooltip
              x={69}
              y={72}
              text="15% excess charge"
              tooltipText="excessCharge"
              direction="left"
            />
            <HotspotTooltip
              x={20}
              y={76}
              text="copay"
              tooltipText="copay"
              direction="left"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Medical Services</h3>
            </div>

            <div className="card-col-container">
              {/* Left Column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    {'Medicare Advantage Plan with Parts A and B Premiums'}
                  </h4>
                </div>

                {/* MA card */}
                <div className="itemized-costs-card ma-plan">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Advantage</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Medical{' '}
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$0'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Medical{' '}
                            <InlineTooltip
                              text="Copays"
                              tooltipText={'copay'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$645'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>Overall Total for 150 days:</p>
                    <p className="total-number">{'$3,494'}</p>
                  </div>
                </div>
              </div>

              {/* Versus column */}
              <div className="versus-circle">
                <h3>vs</h3>
              </div>

              {/* Right Column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$283</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="Coinsurance"
                              tooltipText={'coinsurance'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$2,847'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            <InlineTooltip
                              text="15% Excess Charge Total"
                              tooltipText={'excessCharge'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,394'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>{'Overall Total:'}</p>
                    <p className="total-number">{'$7,959'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Example card - Rx */}
      <div className="lfe-card example-card">
        <div className="lfe-card-header">
          <h3 className="centered">
            Example of How Medicare Advantage Could Apply to Daniel&rsquo;s
            Prescription Needs (in 2026)
          </h3>
          <p className="centered">
            Actual plan costs and details, as well as the costs for
            prescriptions, may vary in your area.
          </p>
        </div>

        <div className="lfe-card-content">
          <div className="example-explainer">
            <p>In this example...</p>
            <div className="example-content">
              <p>Daniel is prescribed 3 different medications:</p>
              <ul className="example-content">
                <li>
                  <b>1</b>{' '}
                  <InlineTooltip text="Tier 1" tooltipText={'rxTiers'} />{' '}
                  Generic Drug
                </li>
                <li>
                  <b>2</b>{' '}
                  <InlineTooltip text="Tier 5" tooltipText={'rxTiers'} />{' '}
                  Brand-Name Drugs
                </li>
              </ul>
            </div>
          </div>

          <figure className="annotated-image">
            <Image
              src={cardImages[3].src}
              alt={cardImages[3].alt}
              width={cardImages[3].width}
              height={cardImages[3].height}
              style={{ width: '100%', height: 'auto' }}
            />
            <figcaption className="screen-reader-only">
              {cardImages[3].caption}
            </figcaption>
            <HotspotTooltip x={24} y={29} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={37}
              y={30}
              text="deductible"
              tooltipText="deductible"
            />
            <HotspotTooltip
              x={60}
              y={23}
              text="coinsurance"
              tooltipText="coinsurance"
              direction="left"
            />
            <HotspotTooltip x={24} y={49} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={52}
              y={43}
              text="coinsurance"
              tooltipText="coinsurance"
            />
            <HotspotTooltip x={24} y={69} text="copay" tooltipText="copay" />
            <HotspotTooltip
              x={52}
              y={64}
              text="coinsurance"
              tooltipText="coinsurance"
            />

            <HotspotTooltip
              x={50}
              y={71}
              text="out-of-pocket max"
              tooltipText="oopm"
            />
          </figure>

          <div className="itemized-costs-container">
            <div className="itemized-container-header">
              <h3>Example Total Costs for Prescription Coverage</h3>
            </div>

            <div className="card-col-container">
              {/* Left column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    {'Medicare Advantage Plan with Parts A and B Premiums'}
                  </h4>
                </div>

                {/* MA card */}
                <div className="itemized-costs-card ma-plan">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Medicare Advantage</caption>
                      <tbody>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$414'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Rx{' '}
                            <InlineTooltip
                              text="Deductible"
                              tooltipText={'deductible'}
                            />
                            :
                          </th>
                          <td>
                            <b>$615</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Rx{' '}
                            <InlineTooltip
                              text="Copays"
                              tooltipText={'copay'}
                            />
                            Total:
                          </th>
                          <td>
                            <b>$90</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">
                            Rx{' '}
                            <InlineTooltip
                              text="Coinsurance"
                              tooltipText={'coinsurance'}
                            />{' '}
                            Total:
                          </th>
                          <td>
                            <b>{'$1,518'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Plus sign */}
                <div className="plus-sign">
                  <PlusSignIcon size="25" color="blue" />
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>{'Overall Total:'}</p>
                    <p className="total-number">{'$5,072'}</p>
                  </div>
                </div>
              </div>

              {/* Versus column */}
              <div className="versus-circle">
                <h3>vs</h3>
              </div>

              {/* Right column */}
              <div className="card-col">
                {/* Header */}
                <div className="card-col-header">
                  <h4>
                    Original Medicare<br></br>Parts A and B (Alone)
                  </h4>
                </div>

                {/* Part A and B card */}
                <div className="itemized-costs-card original-medicare">
                  <div className="itemized-costs-content">
                    <table>
                      <caption>Original Medicare</caption>
                      <tbody>
                        <tr className="section-header">
                          <th scope="colgroup" colSpan={2}>
                            Part A
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>$0</b>
                          </td>
                        </tr>
                        <tr className="section-header part-b">
                          <th scope="colgroup" colSpan={2}>
                            Part B
                          </th>
                        </tr>
                        <tr>
                          <th scope="row">
                            Yearly{' '}
                            <InlineTooltip
                              text="Premium"
                              tooltipText={'premium'}
                            />
                            :
                          </th>
                          <td>
                            <b>{'$2,435'}</b>
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">Uninsured Rx Total:</th>
                          <td>
                            <b>$10,968</b>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Overall Total */}
                <div className="total-container">
                  <div className="total-summary">
                    <p>{'Overall Total:'}</p>
                    <p className="total-number">{'$13,403'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MAExplainer;
